/**
 * The 8-channel BrainBit Pro over raw BLE, through Web Bluetooth (Sprint 1.9.8 S3).
 *
 * The protocol is `docs/pro-protocol.md`, recovered from the recorder's own GATT
 * traffic (`calibrations/backend/eeg.py` through neurosdk2) paired with the
 * sessions it wrote. That document is the authority, not `js-neurosdk2`, whose
 * signal layout is wrong for this device and whose command `1` is stop, not
 * power-down.
 *
 * - Commands are single writes WITH response on `…0003`; the status notification
 *   on `…0002` echoes the last command in byte 3 (error in byte 1, state in 2).
 * - Connect writes the recorder's bytes exactly: `0b 04`, then the amplifier
 *   parameters (Gain3 / ChModeNormal / ChResistUse / GenCurr6nA on all eight).
 *   There is no readback over GATT, so (owner ruling 1) the connect is refused
 *   unless the status echoes `0a` with no error.
 * - Signal (`…0004`, 104 bytes): a uint32 counter stepping by 4, then four
 *   samples of a marker byte and eight int24 LE counts in slot order
 *   O2, T4, T6, Fp2, T5, T3, O1, Fp1. `pack` = counter + sample index, which is
 *   the recorder's `pack_num`; µV is the SDK's constant, bit for bit.
 * - Resistance (`…0005`, 36 bytes): a counter, then eight uint32 ohms. 0 is no
 *   reading (a channel not yet measured, and once a lifted one); 50,000,000 is an
 *   open contact.
 *
 * REFUSE, NEVER REPAIR. Inside one signal run any counter step other than +4, or a
 * malformed frame, is a `fault`: the attempt is over and the page aborts it. So
 * is a contact lifted mid-recording, which the device never reports: the channel
 * saturates (~+752 mV flat on the lifted T3 in run 3), and holding past
 * `SATURATED_UV` for `SATURATED_SAMPLES` is read as `lost`.
 */

import { Emitter, HeadbandError, webBluetoothAvailable, type FaultEvent, type Headband, type HeadbandEvents, type HeadbandInfo, type Sample } from './types'
import type { BluetoothLike } from './brainbitClassic'

const uuid = (n: number) => `7e40000${n}-b534-f393-68a9-e50e24dcca97`
export const PRO_SERVICE_UUID = uuid(1)
export const PRO_STATUS_UUID = uuid(2)
export const PRO_COMMAND_UUID = uuid(3)
export const PRO_SIGNAL_UUID = uuid(4)
export const PRO_RESIST_UUID = uuid(5)
const DEVICE_INFORMATION = 0x180a
const SERIAL_NUMBER = 0x2a25

export const CMD_STOP = 0x01
export const CMD_SIGNAL = 0x03
export const CMD_RESIST = 0x04
/** The SDK's second parameter write at connect, sent as the recorder sends it (probably 250 Hz; see the protocol doc). */
export const CMD_SAMPLING = new Uint8Array([0x0b, 0x04])
/** The recorder's amplifier write, byte for byte: (mode 1, gain 06) × 8, resist-use 1 × 8, current 1. */
export const AMP_CONFIG = new Uint8Array([0x0a, ...Array(8).fill([0x01, 0x06]).flat(), ...Array(8).fill(0x01), 0x01])

/** The archive's order, the recorder's `hardware_channel_names`. */
export const PRO_CHANNELS = ['Fp1', 'Fp2', 'T3', 'T4', 'T5', 'T6', 'O1', 'O2'] as const
/** Which electrode each packet slot carries (the recorder's `sdk_num` map). */
export const PRO_SLOTS = ['O2', 'T4', 'T6', 'Fp2', 'T5', 'T3', 'O1', 'Fp1'] as const
const SLOT_OF = PRO_CHANNELS.map((ch) => PRO_SLOTS.indexOf(ch))

/** counts → µV: the SDK's float32 2.4 V reference over 2²³ − 1 at gain 3, in double precision. */
export const UV_PER_COUNT = (Math.fround(2.4) / (3 * 8388607)) * 1e6
export const SAMPLING_RATE = 250
export const SIGNAL_FRAME_BYTES = 104
export const RESIST_FRAME_BYTES = 36
/** The device's resistance for an open contact. */
export const OPEN_CONTACT_OHMS = 50_000_000
/** Above this |µV| a channel is saturated; normal offsets on the owner's device sat within ±50 mV. */
export const SATURATED_UV = 500_000
/** One second of saturation on any channel is a lifted contact. */
export const SATURATED_SAMPLES = SAMPLING_RATE
/**
 * The contact check at Start (criterion 8): resistance cannot tell a lifted Pro
 * contact from a seated one (run 3: a lifted T3 read 120–300 kΩ, a seated O2 670 kΩ),
 * but its signal saturates at once. The check listens this long, inside the
 * second the mid-signal fault needs, so it answers first.
 */
export const CONTACT_CHECK_MS = 600
/** A channel saturated for at least this share of the check is lifted. */
export const CONTACT_CHECK_SHARE = 0.5
/** The device's status write arrives within ~60 ms; generous for Chrome. */
const ECHO_TIMEOUT_MS = 2000
/** Start-signal → first frame. Longer than this, and the attempt never began. */
export const FIRST_FRAME_MS = 3000
/** STOP → next command: the recorder's 0.5 s `MODE_SETTLE_SECONDS`. */
const MODE_SETTLE_MS = 500
/** After a STOP the next status reads battery 100 once (protocol doc); ignore battery this long. */
const BATTERY_QUIET_MS = 500

const delay = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))
const bytesOf = (v: DataView | Uint8Array) => (v instanceof DataView ? new Uint8Array(v.buffer, v.byteOffset, v.byteLength) : v)
const hex = (b: Uint8Array) => Array.from(b, (x) => x.toString(16).padStart(2, '0')).join(' ')

export interface ProSignalFrame {
  counter: number
  /** Four samples, values in µV in `PRO_CHANNELS` order. */
  samples: number[][]
}

/** Decode one `…0004` notification. Throws on any length but 104. */
export function decodeProSignal(data: DataView | Uint8Array): ProSignalFrame {
  const d = bytesOf(data)
  if (d.length !== SIGNAL_FRAME_BYTES) throw new HeadbandError('protocol', `expected a ${SIGNAL_FRAME_BYTES}-byte signal frame, got ${d.length}`)
  const view = new DataView(d.buffer, d.byteOffset, d.byteLength)
  const samples: number[][] = []
  for (let s = 0; s < 4; s++) {
    const base = 4 + 25 * s + 1
    const slots: number[] = []
    for (let k = 0; k < 8; k++) {
      const o = base + 3 * k
      let c = d[o]! | (d[o + 1]! << 8) | (d[o + 2]! << 16)
      if (c & 0x800000) c -= 0x1000000
      slots.push(c * UV_PER_COUNT)
    }
    samples.push(SLOT_OF.map((i) => slots[i]!))
  }
  return { counter: view.getUint32(0, true), samples }
}

export interface ProResistFrame {
  counter: number
  /** Ohms per channel in `PRO_CHANNELS` order, as the device sent them (0 = being measured). */
  ohms: number[]
}

/** Decode one `…0005` notification. Throws on any length but 36. */
export function decodeProResist(data: DataView | Uint8Array): ProResistFrame {
  const d = bytesOf(data)
  if (d.length !== RESIST_FRAME_BYTES) throw new HeadbandError('protocol', `expected a ${RESIST_FRAME_BYTES}-byte resistance frame, got ${d.length}`)
  const view = new DataView(d.buffer, d.byteOffset, d.byteLength)
  const slots = Array.from({ length: 8 }, (_, k) => view.getUint32(4 + 4 * k, true))
  return { counter: view.getUint32(0, true), ohms: SLOT_OF.map((i) => slots[i]!) }
}

/** Ω → kΩ once, as the recorder's `_resist_cb` does; the open-contact value is +∞ there too. */
export function kohmOf(ohms: number): number {
  return ohms >= OPEN_CONTACT_OHMS ? Infinity : ohms / 1000
}

/** The next counter a gap-free signal run must carry, uint32 wrap included. */
export function nextCounter(counter: number): number {
  return (counter + 4) % 0x1_0000_0000
}

export interface ProOptions {
  /** A device already chosen in the page's one chooser (S4): no second chooser, no lost gesture. */
  device?: BluetoothDevice
}

type Mode = 'idle' | 'signal' | 'resist'

export class WebBluetoothBrainBitPro implements Headband {
  private readonly emitter = new Emitter()
  private readonly faultHandlers = new Set<(e: FaultEvent) => void>()
  private readonly bluetooth: BluetoothLike | null
  private device: BluetoothDevice | null
  private status: BluetoothRemoteGATTCharacteristic | null = null
  private command: BluetoothRemoteGATTCharacteristic | null = null
  private signal: BluetoothRemoteGATTCharacteristic | null = null
  private resist: BluetoothRemoteGATTCharacteristic | null = null
  private _info: HeadbandInfo | null = null
  private mode: Mode = 'idle'
  /** The counter the next signal frame must carry; null until the run's first frame. */
  private expected: number | null = null
  /** A fault ends the run: nothing more is emitted until the next start. */
  private faulted = false
  private saturatedFor = PRO_CHANNELS.map(() => 0)
  private batteryQuietUntil = 0
  private echoWaiters: { byte: number; resolve: (status: Uint8Array) => void }[] = []
  private firstFrame: (() => void) | null = null
  private packets = 0
  private lastPacketAt: number | null = null
  private statusFrames = 0
  private lastStatusHex: string | null = null
  private amplifier: string | null = null

  constructor(bluetooth?: BluetoothLike, options: ProOptions = {}) {
    this.bluetooth = bluetooth ?? (webBluetoothAvailable() ? navigator.bluetooth : null)
    this.device = options.device ?? null
  }

  get info(): HeadbandInfo | null {
    return this._info
  }

  on<K extends keyof HeadbandEvents>(event: K, handler: (payload: HeadbandEvents[K]) => void): () => void {
    return this.emitter.on(event, handler)
  }

  onFault(handler: (payload: FaultEvent) => void): () => void {
    this.faultHandlers.add(handler)
    return () => {
      this.faultHandlers.delete(handler)
    }
  }

  async connect(): Promise<HeadbandInfo> {
    let device = this.device
    if (!device) {
      if (!this.bluetooth) throw new HeadbandError('unsupported', 'Web Bluetooth is not available in this browser.')
      try {
        device = await this.bluetooth.requestDevice({
          filters: [{ namePrefix: 'BB Pro' }, { services: [PRO_SERVICE_UUID] }],
          optionalServices: [PRO_SERVICE_UUID, DEVICE_INFORMATION],
        })
      } catch (err) {
        if ((err as { name?: string })?.name === 'NotFoundError') throw new HeadbandError('cancelled', 'No headband was chosen.')
        throw new HeadbandError('connect_failed', (err as Error)?.message ?? 'Could not open the Bluetooth chooser.')
      }
    }
    this.device = device
    device.addEventListener('gattserverdisconnected', this.onDisconnected)
    let serial: string | null
    try {
      const server = await device.gatt!.connect()
      const service = await server.getPrimaryService(PRO_SERVICE_UUID)
      this.status = await service.getCharacteristic(PRO_STATUS_UUID)
      this.command = await service.getCharacteristic(PRO_COMMAND_UUID)
      this.signal = await service.getCharacteristic(PRO_SIGNAL_UUID)
      this.resist = await service.getCharacteristic(PRO_RESIST_UUID)
      serial = await this.readSerial(server)
      // Status first (the SDK's order), then both data streams, routed by mode.
      this.status.addEventListener('characteristicvaluechanged', this.onStatus)
      await this.status.startNotifications()
      this.signal.addEventListener('characteristicvaluechanged', this.onSignal)
      await this.signal.startNotifications()
      this.resist.addEventListener('characteristicvaluechanged', this.onResist)
      await this.resist.startNotifications()
    } catch (err) {
      await this.teardown()
      throw new HeadbandError('connect_failed', (err as Error)?.message ?? 'The headband did not answer.')
    }
    try {
      await this.command_(CMD_SAMPLING)
      await this.command_(AMP_CONFIG)
    } catch (err) {
      await this.teardown()
      throw err instanceof HeadbandError ? err : new HeadbandError('protocol', (err as Error)?.message ?? 'The amplifier was not configured.')
    }
    this.amplifier = hex(AMP_CONFIG)
    this._info = {
      family: 'brainbit_pro',
      name: device.name ?? 'BrainBit Pro',
      channels: PRO_CHANNELS,
      serial,
      battery: null,
      sensFamily: 'SensorFamily.LEBrainBitPro',
      estimatedQuality: false,
      samplingRate: SAMPLING_RATE,
    }
    this.emitter.emit('status', { at: performance.now(), connected: true, battery: null })
    return this._info
  }

  private async readSerial(server: BluetoothRemoteGATTServer): Promise<string | null> {
    try {
      const dis = await server.getPrimaryService(DEVICE_INFORMATION)
      const value = await (await dis.getCharacteristic(SERIAL_NUMBER)).readValue()
      return new TextDecoder().decode(value).replace(/\0+$/, '') || null
    } catch {
      return null
    }
  }

  /**
   * One acknowledged write, then the status echo of its first byte with no
   * error. A rejected write, a missing echo or an error byte refuses.
   */
  private async command_(bytes: Uint8Array): Promise<void> {
    if (!this.command) throw new HeadbandError('protocol', 'not connected')
    const op = bytes[0]!
    const echo = new Promise<Uint8Array>((resolve) => this.echoWaiters.push({ byte: op, resolve }))
    let timer: ReturnType<typeof setTimeout> | undefined
    const timeout = new Promise<null>((resolve) => {
      timer = setTimeout(() => resolve(null), ECHO_TIMEOUT_MS)
    })
    if (op === CMD_STOP) this.batteryQuietUntil = performance.now() + BATTERY_QUIET_MS
    try {
      await this.command.writeValueWithResponse(bytes as unknown as BufferSource)
    } catch (err) {
      clearTimeout(timer)
      this.echoWaiters = this.echoWaiters.filter((w) => w.byte !== op)
      throw new HeadbandError('protocol', `the headband refused command ${op.toString(16)}: ${(err as Error)?.message ?? err}`)
    }
    const status = await Promise.race([echo, timeout])
    clearTimeout(timer)
    this.echoWaiters = this.echoWaiters.filter((w) => w.byte !== op)
    if (!status) throw new HeadbandError('protocol', `the headband did not confirm command ${op.toString(16).padStart(2, '0')}`)
    if (status[1] !== 0) throw new HeadbandError('protocol', `the headband reported error ${status[1]} for command ${op.toString(16).padStart(2, '0')}`)
  }

  private readonly onStatus = (ev: Event) => {
    const value = (ev.target as BluetoothRemoteGATTCharacteristic).value
    if (!value || value.byteLength < 4) return
    const b = new Uint8Array(value.buffer, value.byteOffset, value.byteLength).slice()
    this.statusFrames++
    this.lastStatusHex = hex(b)
    for (const w of this.echoWaiters) if (w.byte === b[3]) w.resolve(b)
    if (performance.now() >= this.batteryQuietUntil && b[0]! <= 100) {
      if (this._info) this._info.battery = b[0]!
      this.emitter.emit('status', { at: performance.now(), connected: true, battery: b[0]! })
    }
  }

  private readonly onSignal = (ev: Event) => {
    const value = (ev.target as BluetoothRemoteGATTCharacteristic).value
    if (!value || this.mode !== 'signal' || this.faulted) return
    this.packets++
    const at = performance.now()
    this.lastPacketAt = at
    let frame: ProSignalFrame
    try {
      frame = decodeProSignal(value)
    } catch (err) {
      this.fault((err as Error).message)
      return
    }
    if (this.expected !== null && frame.counter !== this.expected) {
      this.fault(`signal counter ${frame.counter} where ${this.expected} was due: data was lost, repeated or reordered`)
      return
    }
    this.expected = nextCounter(frame.counter)
    const samples: Sample[] = frame.samples.map((values, i) => ({ pack: frame.counter + i, values }))
    const lifted = this.saturation(frame.samples)
    if (lifted) {
      this.fault(`the ${lifted} contact lost the skin (its signal saturated)`, 'contact_lost')
      return
    }
    this.emitter.emit('samples', { at, samples })
    const wake = this.firstFrame
    this.firstFrame = null
    wake?.()
  }

  /** The channel that has been saturated for `SATURATED_SAMPLES` running, if any. */
  private saturation(samples: number[][]): string | null {
    for (const values of samples) {
      for (let c = 0; c < values.length; c++) {
        this.saturatedFor[c] = Math.abs(values[c]!) >= SATURATED_UV ? this.saturatedFor[c]! + 1 : 0
        if (this.saturatedFor[c]! >= SATURATED_SAMPLES) return PRO_CHANNELS[c]!
      }
    }
    return null
  }

  private readonly onResist = (ev: Event) => {
    const value = (ev.target as BluetoothRemoteGATTCharacteristic).value
    if (!value || this.mode !== 'resist' || this.faulted) return
    let frame: ProResistFrame
    try {
      frame = decodeProResist(value)
    } catch (err) {
      this.fault((err as Error).message)
      return
    }
    const readings: Record<string, number | null> = {}
    // 0 is the device saying it has no reading; the recorder stores it as 0 kΩ,
    // which the quality words would call Good. Here it is no reading.
    PRO_CHANNELS.forEach((ch, c) => {
      readings[ch] = frame.ohms[c] === 0 ? null : kohmOf(frame.ohms[c]!)
    })
    this.emitter.emit('resist', { at: performance.now(), readings, estimated: false })
  }

  private fault(message: string, reason?: FaultEvent['reason']): void {
    if (this.faulted) return
    this.faulted = true
    console.warn('[brainbit-pro] fault:', message)
    const event: FaultEvent = { at: performance.now(), message, ...(reason ? { reason } : {}) }
    for (const h of [...this.faultHandlers]) h(event)
  }

  private readonly onDisconnected = () => {
    this.mode = 'idle'
    this.emitter.emit('status', { at: performance.now(), connected: false, battery: null })
  }

  /** STOP, the settle, then the mode's command, each confirmed. */
  private async enter(mode: Exclude<Mode, 'idle'>): Promise<void> {
    if (!this.device?.gatt?.connected) throw new HeadbandError('protocol', 'not connected')
    this.mode = 'idle'
    await this.command_(new Uint8Array([CMD_STOP]))
    await delay(MODE_SETTLE_MS)
    this.faulted = false
    this.expected = null
    this.saturatedFor = PRO_CHANNELS.map(() => 0)
    this.mode = mode
    await this.command_(new Uint8Array([mode === 'signal' ? CMD_SIGNAL : CMD_RESIST]))
  }

  /**
   * The contact check at Start: listen to the signal for `CONTACT_CHECK_MS` and
   * name every channel saturated for at least `CONTACT_CHECK_SHARE` of it.
   * Signal must already be streaming.
   */
  async checkContacts(ms = CONTACT_CHECK_MS): Promise<string[]> {
    if (this.mode !== 'signal') throw new HeadbandError('protocol', 'the contact check needs the signal')
    const saturated = PRO_CHANNELS.map(() => 0)
    let total = 0
    const off = this.emitter.on('samples', (e) => {
      for (const sample of e.samples) {
        total++
        sample.values.forEach((v, c) => {
          if (Math.abs(v) >= SATURATED_UV) saturated[c]!++
        })
      }
    })
    try {
      await delay(ms)
    } finally {
      off()
    }
    if (total === 0) throw new HeadbandError('protocol', 'no signal during the contact check')
    return PRO_CHANNELS.filter((_, c) => saturated[c]! >= total * CONTACT_CHECK_SHARE)
  }

  /** Resolves on the run's first frame; throws (and faults) if none comes within `FIRST_FRAME_MS`. */
  async startSignal(): Promise<void> {
    const first = new Promise<boolean>((resolve) => {
      this.firstFrame = () => resolve(true)
      setTimeout(() => resolve(false), FIRST_FRAME_MS)
    })
    await this.enter('signal')
    if (!(await first)) {
      this.firstFrame = null
      const message = `no signal from the headband within ${FIRST_FRAME_MS / 1000} s`
      this.fault(message)
      throw new HeadbandError('protocol', message)
    }
  }

  async stopSignal(): Promise<void> {
    if (this.mode !== 'signal') return
    this.mode = 'idle'
    await this.command_(new Uint8Array([CMD_STOP])).catch(() => undefined)
  }

  async startResist(): Promise<void> {
    await this.enter('resist')
  }

  async stopResist(): Promise<void> {
    if (this.mode !== 'resist') return
    this.mode = 'idle'
    await this.command_(new Uint8Array([CMD_STOP])).catch(() => undefined)
  }

  /** Diagnostics for the Headband card, the classic's shape plus the confirmed amplifier write (S6). */
  stats(): { packets: number; lastPacketAt: number | null; statusFrames: number; lastStatusHex: string | null; amplifier: string | null } {
    return { packets: this.packets, lastPacketAt: this.lastPacketAt, statusFrames: this.statusFrames, lastStatusHex: this.lastStatusHex, amplifier: this.amplifier }
  }

  private async teardown(): Promise<void> {
    this.mode = 'idle'
    this.firstFrame = null
    try {
      this.signal?.removeEventListener('characteristicvaluechanged', this.onSignal)
      this.resist?.removeEventListener('characteristicvaluechanged', this.onResist)
      this.status?.removeEventListener('characteristicvaluechanged', this.onStatus)
      if (this.device?.gatt?.connected) {
        await this.command?.writeValueWithResponse(new Uint8Array([CMD_STOP]) as unknown as BufferSource).catch(() => undefined)
        this.device.gatt.disconnect()
      }
    } finally {
      this.device?.removeEventListener('gattserverdisconnected', this.onDisconnected)
      this.device = null
      this.status = this.command = this.signal = this.resist = null
    }
  }

  async disconnect(): Promise<void> {
    await this.teardown()
    this._info = null
    this.emitter.emit('status', { at: performance.now(), connected: false, battery: null })
  }
}
